class Utils {
  // Formats a given amount as a dollar currency string.

  // True on phone/tablet hardware regardless of the reported viewport
  // width. Width-only checks (max-width: 800px) fail on Android when the
  // page loads in landscape, display zoom is on, or "Desktop site" is
  // enabled — which exposed desktop-only UI (the employee switcher) on
  // phones. The UA regex catches normal mobile browsers; the
  // coarse-pointer check catches "Desktop site" mode, which spoofs a
  // desktop UA but cannot change the primary input from touch.
  static isMobileDevice = () => {
    const isMobileUA =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(
        navigator.userAgent
      );
    const isTouchOnly =
      window.matchMedia('(pointer: coarse)').matches &&
      navigator.maxTouchPoints > 0;
    return isMobileUA || isTouchOnly;
  };

  static isValidColor = (color) => {
    const s = new Option().style;
    s.color = color;
    return s.color !== '';
  };
  static formatDollar(amount) {
    // Regex expression taken from https://stackoverflow.com/questions/2901102
    return '$' + amount.replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',');
  }

  static formatTodayDate = (date) => {
    const options = {
      timeZone: 'America/Chicago',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    };

    const formatter = new Intl.DateTimeFormat('en-US', options);
    const parts = formatter.formatToParts(date);

    // Extract year, month, and day parts
    const year = parts.find((part) => part.type === 'year').value;
    const month = parts.find((part) => part.type === 'month').value;
    const day = parts.find((part) => part.type === 'day').value;

    // Format as YYYY-MM-DD
    return `${year}-${month}-${day}`;
  };

  //10/26/2024 4:53 p.m.
  static formatDateMMDDYYYYTime = (dateString) => {
    // Parse the date string
    const date = new Date(
      dateString.replace(/(a\.m\.|p\.m\.)/i, 'AM').replace(/(p\.m\.)/i, 'PM')
    );

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  static convertTo12HourFormat = (time24) => {
    // Check if the input time is valid
    if (!/^\d{2}:\d{2}:\d{2}$/.test(time24)) {
      throw new Error('Invalid time format. Expected HH:mm:ss.');
    }

    // Create a new Date object and set hours, minutes, and seconds
    const [hours, minutes, seconds] = time24.split(':').map(Number);
    const date = new Date();
    date.setHours(hours);
    date.setMinutes(minutes);
    date.setSeconds(seconds);

    // Format the time using options
    const options = {
      hour: '2-digit',
      minute: '2-digit',
      // second: '2-digit',
      hour12: true, // Use 12-hour clock with AM/PM
    };

    // Convert to 12-hour format
    return date.toLocaleTimeString('en-US', options);
  };

  //2025-06-05T16:14:41Z -> "2025-06-05 11:14 AM"
  static formatDateTime = (timestamp) => {
    if (
      !timestamp ||
      typeof timestamp !== 'string' ||
      timestamp === 'No previous reading date available.'
    ) {
      return 'No previous reading date available.';
    }

    const dateObj = new Date(timestamp);

    if (isNaN(dateObj.getTime())) {
      return 'No previous reading date available.';
    }

    // Format date: YYYY-MM-DD
    const dateFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Chicago',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });

    const parts = dateFormatter.formatToParts(dateObj);
    const year = parts.find((p) => p.type === 'year')?.value;
    const month = parts.find((p) => p.type === 'month')?.value;
    const day = parts.find((p) => p.type === 'day')?.value;

    // Format time: hh:mm AM/PM
    const timeFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Chicago',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });

    const time = timeFormatter.format(dateObj);

    return `${year}-${month}-${day} ${time}`;
  };

  static handlePdfAction = (pdfData, action, fileName) => {
    if (!pdfData) return;
    const isMobileApp = window.Toaster && window.Toaster.postMessage;

    if (isMobileApp) {
      const uint8 = new Uint8Array(pdfData);
      let binary = '';

      // ✅ Chunked conversion — safe for large PDFs in mobile WebViews
      const chunk = 8192;
      for (let i = 0; i < uint8.length; i += chunk) {
        binary += String.fromCharCode.apply(null, uint8.subarray(i, i + chunk));
      }

      try {
        const base64String = btoa(binary);
        const mobileAction = action === 'download' ? 'open' : action;

        window.Toaster.postMessage(
          JSON.stringify({
            action: mobileAction,
            base64: base64String,
            fileName: fileName,
          })
        );
      } catch (e) {
        console.error('Base64 Encoding Error:', e);
      }
    } else {
      // FALLBACK: Web Logic
      const blob = new Blob([pdfData], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);

      if (action === 'download') {
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      } else {
        Utils.printPdfBlob(blobUrl);
      }
    }
  };

  /**
   * Full-screen "Preparing print…" veil, shown from the click until the
   * browser's own print dialog takes over.
   *
   * Printing is not instant: the PDF has to load into the iframe and the
   * viewer has to attach before the dialog appears — a second or more on a
   * large report, during which the click looked ignored. Built out of plain
   * DOM rather than a React component so every caller of handlePdfAction
   * inherits it without threading busy state through a dozen screens.
   *
   * Returns the hide function; calling it twice is harmless.
   */
  static showPrintOverlay = (message) => {
    const STYLE_ID = 'nuve-print-overlay-style';
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent =
        '@keyframes nuve-print-spin{to{transform:rotate(360deg)}}';
      document.head.appendChild(style);
    }

    const overlay = document.createElement('div');
    overlay.setAttribute('role', 'status');
    overlay.setAttribute('aria-live', 'polite');
    // Above the full-screen MUI preview dialog (zIndex 1300) it is launched from.
    overlay.style.cssText =
      'position:fixed;top:0;left:0;right:0;bottom:0;z-index:20000;' +
      'display:flex;flex-direction:column;align-items:center;' +
      'justify-content:center;gap:16px;background:rgba(15,23,42,0.55);' +
      'color:#fff;font-size:15px;letter-spacing:0.02em;';

    const spinner = document.createElement('div');
    spinner.style.cssText =
      'width:44px;height:44px;border:3px solid rgba(255,255,255,0.25);' +
      'border-top-color:#fff;border-radius:50%;' +
      'animation:nuve-print-spin 0.8s linear infinite;';

    const label = document.createElement('div');
    label.textContent = message || 'Preparing print…';

    overlay.appendChild(spinner);
    overlay.appendChild(label);
    document.body.appendChild(overlay);

    let hidden = false;
    const hide = () => {
      if (hidden) return;
      hidden = true;
      clearTimeout(guard);
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    };
    // Never leave the app veiled if something below goes sideways.
    const guard = setTimeout(hide, 15000);

    return hide;
  };

  /**
   * Print a PDF from a blob URL through a throwaway iframe.
   *
   * Edge will not print from a `display: none` iframe: its embedded PDF viewer
   * never initializes inside an unrendered frame, so by the time `print()` is
   * called there is no document behind it and the click does nothing at all —
   * no dialog, no error. Chrome is lenient about this, which is why the old
   * hidden-iframe version only ever failed in Edge. The frame therefore has to
   * be laid out (real size, off-screen, transparent) rather than hidden.
   *
   * Teardown is driven off the window regaining focus — the print dialog is
   * modal, so that is the one reliable signal that it has closed. Ripping the
   * iframe out on a short fixed timer could cancel a dialog that was still
   * opening on a slower machine.
   *
   * If the frame never loads (Edge set to always download PDFs) or `print()`
   * is refused, the PDF is opened in a new tab so the user can print from the
   * browser's own viewer instead of getting silence.
   */
  static printPdfBlob = (blobUrl) => {
    const hideOverlay = Utils.showPrintOverlay();

    // Kept alive well past the print dialog: revoking early leaves the viewer —
    // or the fallback tab — pointing at a dead URL.
    const release = () => setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);

    const openInNewTab = () => {
      hideOverlay();
      const win = window.open(blobUrl, '_blank');
      if (!win) {
        // Popup blocked — at least hand the file over.
        const link = document.createElement('a');
        link.href = blobUrl;
        link.target = '_blank';
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      release();
    };

    const iframe = document.createElement('iframe');
    iframe.title = 'print';
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText =
      'position:fixed;left:-10000px;top:0;width:1024px;height:768px;' +
      'border:0;opacity:0;pointer-events:none;';

    let settled = false; // print() attempted, or we gave up on the frame
    let removed = false;

    const removeFrame = () => {
      if (removed) return;
      removed = true;
      window.removeEventListener('focus', onWindowFocus);
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      release();
    };

    const onWindowFocus = () => setTimeout(removeFrame, 500);

    const giveUp = () => {
      if (settled) return;
      settled = true;
      removeFrame();
      openInNewTab();
    };

    const loadTimer = setTimeout(giveUp, 8000);

    iframe.onload = () => {
      clearTimeout(loadTimer);
      if (settled) return;
      // Edge needs a beat after load for the PDF viewer to attach itself to the
      // frame; print() fired immediately can hit an empty document.
      setTimeout(() => {
        if (settled) return;
        settled = true;
        // Drop the veil and let it actually paint before print() runs: the
        // dialog blocks the main thread, so anything left on screen freezes
        // there with it until the dialog is dismissed.
        hideOverlay();
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            try {
              iframe.contentWindow.focus();
              iframe.contentWindow.print();
              window.addEventListener('focus', onWindowFocus);
              setTimeout(removeFrame, 120000); // safety net if focus never fires
            } catch (e) {
              removeFrame();
              openInNewTab();
            }
          })
        );
      }, 300);
    };
    iframe.onerror = giveUp;

    document.body.appendChild(iframe);
    iframe.src = blobUrl;
  };

  // static handlePdfAction = (pdfData, action, fileName) => {
  //   if (!pdfData) return;
  //   const isMobileApp = window.Toaster && window.Toaster.postMessage;
  //
  //   if (isMobileApp) {
  //     // Convert Uint8Array to Base64 string for the Flutter Bridge
  //     const base64String = btoa(
  //       new Uint8Array(pdfData).reduce(
  //         (data, byte) => data + String.fromCharCode(byte),
  //         ''
  //       )
  //     );
  //
  //     const mobileAction = action === 'download' ? 'open' : action;
  //
  //     window.Toaster.postMessage(
  //       JSON.stringify({
  //         action: mobileAction, // 'print' or 'download'
  //         base64: base64String,
  //         fileName: fileName,
  //       })
  //     );
  //   } else {
  //     // FALLBACK: Your existing Web Logic
  //     const blob = new Blob([Uint8Array.from(pdfData)], {
  //       type: 'application/pdf',
  //     });
  //     const blobUrl = URL.createObjectURL(blob);
  //
  //     if (action === 'download') {
  //       let link = document.createElement('a');
  //       link.href = blobUrl;
  //       link.download = fileName;
  //       link.click();
  //       URL.revokeObjectURL(blobUrl);
  //     } else {
  //       // Existing Iframe Print Logic
  //       const iframe = document.createElement('iframe');
  //       iframe.style.display = 'none';
  //       iframe.src = blobUrl;
  //       document.body.appendChild(iframe);
  //       iframe.onload = () => {
  //         iframe.contentWindow.focus();
  //         iframe.contentWindow.print();
  //         setTimeout(() => {
  //           URL.revokeObjectURL(blobUrl);
  //           document.body.removeChild(iframe);
  //         }, 1000);
  //       };
  //     }
  //   }
  // };
}

export default Utils;
